/**
 * tutorViewModel — pure, framework-free mapping/formatting functions shared
 * between the web client (`useTutorConversation`, `TutorOverlay`) and the
 * mobile client (TASK-019 Phase 4 "Package condivisi — estrazione tutor").
 * Moved from the web client's `pages/reader/tutor/tutorViewModel.ts`.
 */

import type { TutorActionEvent } from './stream.js'
import type { TutorActionView, TutorFocusBlock, TutorMessage, TutorViewMessage } from './types.js'

// ---------------------------------------------------------------------------
// View-model mapping — persisted TutorMessage -> TutorViewMessage.
// ---------------------------------------------------------------------------

export function actionFromToolData(message: TutorMessage): TutorActionView | null {
  const toolData = message.tool_data
  if (toolData == null) return null

  if (toolData.name === 'open_section' && typeof toolData.input['section_id'] === 'string') {
    return { kind: 'open_section', sectionId: toolData.input['section_id'] }
  }
  // launch_quiz: the QuizSession it created is now persisted as
  // `tool_data.quiz_session_id` (Phase 2 fix) — actionable when present.
  // Older rows saved before that fix omit it and stay informational-only.
  if (toolData.name === 'launch_quiz') {
    return toolData.quiz_session_id != null
      ? { kind: 'launch_quiz', quizSessionId: toolData.quiz_session_id }
      : { kind: 'launch_quiz_unavailable' }
  }
  return null
}

/**
 * Persisted `TutorMessage[]` (as returned by the JSON API's
 * `fetchTutorConversation`, or — TASK-014 Step B2 — Inertia's `tutor.active`
 * prop, which mirrors that same wire shape exactly, see `./types.ts`) ->
 * displayable `TutorViewMessage[]`: drops the non-chat agentic-loop rows
 * (function_call/function_call_output/reasoning) and maps the rest. Shared
 * by `useTutorConversation`'s own load effect and any caller that seeds it
 * with an already-fetched/already-rendered list, so there is only ONE place
 * this filter+map pair is written.
 */
export function toViewMessages(messages: TutorMessage[]): TutorViewMessage[] {
  return messages.filter((m) => m.role === 'user' || m.role === 'assistant').map(toViewMessage)
}

export function toViewMessage(message: TutorMessage): TutorViewMessage {
  return {
    id: message.id,
    role: message.role === 'user' ? 'user' : 'assistant',
    content: message.content,
    citations: (message.citations ?? []).map((c) => ({
      section_id: c.section_id,
      sub_para_id: c.sub_para_id,
      block_id: c.block_id,
      title: c.title,
      quote: c.quote,
    })),
    action: actionFromToolData(message),
    pending: false,
    // A persisted message is by definition a finished turn — no tool is
    // ever "in progress" for it.
    toolStep: null,
    feedback: message.feedback,
    // Loaded straight from the server -> always has a real, feedback-able id.
    persisted: true,
  }
}

export function viewActionFromEvent(action: TutorActionEvent | null): TutorActionView | null {
  if (action == null) return null
  return action.name === 'launch_quiz'
    ? { kind: 'launch_quiz', quizSessionId: action.quiz_session_id }
    : { kind: 'open_section', sectionId: action.section_id }
}

/**
 * Toggle math for the 👍/👎 vote: clicking the already-active vote clears it
 * (back to null); clicking the other one switches to it. Extracted as a
 * pure function so the toggle semantics are unit-tested without rendering
 * the surrounding UI.
 */
export function nextFeedbackValue(
  current: 'up' | 'down' | null,
  clicked: 'up' | 'down',
): 'up' | 'down' | null {
  return current === clicked ? null : clicked
}

/** TASK-013 Phase B — the block a "Chat sul blocco" click was fired from.
 *  Camel-case here (app-domain shape shared by the tutor sidebar/drawer),
 *  converted to the wire's snake_case `TutorFocusBlock` shape only at the
 *  `tutorApi.ts` boundary. */
export interface TutorOverlayFocusBlock {
  blockId: string
  subParaId: string
}

/**
 * Maps this app's own camelCase `focusBlock` shape to the wire's snake_case
 * `TutorFocusBlock` body createTutorConversation sends. Extracted as a pure
 * function (same rationale as `nextFeedbackValue` above): a silent field-name
 * mismatch here would NOT throw — the backend treats an unresolvable/
 * malformed focus_block as ambient context and just drops it (see
 * Tutor::ConversationsController#focus_block_attrs) — so this is exactly the
 * kind of bug that stays invisible without a direct test.
 */
export function focusBlockPayload(
  focusBlock: TutorOverlayFocusBlock | null,
): TutorFocusBlock | undefined {
  return focusBlock != null
    ? { block_id: focusBlock.blockId, sub_para_id: focusBlock.subParaId }
    : undefined
}

export function formatResetsAt(iso: string): string {
  try {
    return new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit' }).format(
      new Date(iso),
    )
  } catch {
    return iso
  }
}

export function formatConversationTimestamp(iso: string): string {
  try {
    const date = new Date(iso)
    const sameDay = date.toDateString() === new Date().toDateString()
    return sameDay
      ? new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit' }).format(date)
      : new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: '2-digit' }).format(date)
  } catch {
    return ''
  }
}
